import React, { useState, useEffect, useRef } from "react";
// StickyHeading Component ----------------------------------------------------
/**
 * Renders its children inside a sticky container and changes background
 * color once the element becomes “stuck”.
 *
 * Props:
 *  - stickyBg (string): background color applied while the element is stuck.
 *    Defaults to "transparent".
 *  - children (ReactNode): heading/content to render.
 *  - className (string): extra Tailwind/CSS classes to append to the sticky
 *    container. Optional.
 *  - stuckClassName (string): extra Tailwind/CSS classes applied only when the
 *    element is stuck. Optional.
 *  - stuckStyle (object): inline style object applied **only** when the
 *    element is stuck. Optional.
 *  - observerOptions (object): extra options passed to IntersectionObserver
 *    (e.g. { root, rootMargin, threshold }). Merged with the default
 *    `{ threshold: 1 }`. Optional.
 */
const StickyHeading = ({
  stickyBg = "transparent",
  className = "",
  stuckClassName = "",
  stuckStyle = {},
  observerOptions = {},
  root = null,
  style = {},
  children,
}) => {
  const [isStuck, setIsStuck] = useState(false);
  const sentinelRef = useRef(null);

  useEffect(() => {
    const options = { threshold: 1, root, ...observerOptions };
    const observer = new IntersectionObserver(([entry]) => {
      // When the sentinel is NOT intersecting, the heading has reached
      // the top offset and is therefore “stuck”.
      setIsStuck(!entry.isIntersecting);
    }, options);

    if (sentinelRef.current) observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      {/* Invisible element that tells us when the heading is stuck */}
      <span ref={sentinelRef} aria-hidden="true" />
      <div
        className={`sticky top-16 z-[99] transition-colors duration-300 ${className} ${
          isStuck ? stuckClassName + "backdrop-blur-md" : ""
        }`}
        style={{
          ...style,
          ...(isStuck ? stuckStyle : {}),
          ...(isStuck ? { backgroundColor: stickyBg+"cc" } : {}),
        }}
      >
        {children}
      </div>
    </>
  );
};

export default StickyHeading;
